import { api, downloadFile } from '@/shared/api/client'
import { qs, type QueryValue } from '@/shared/lib/query'
import type {
  CursorPage, SegmentPreviewResponse, VoterConsentEntity, VoterContactEntity,
  VoterDetailResponse, VoterDossierResponse, VoterHouseholdEntity, VoterPiiResponse,
  VoterPlacementHistoryEntity, VoterResponse, VoterSegmentEntity, VoterStatisticsResponse,
  VoterDuplicateCandidateEntity, VoterExportJobEntity, VoterImportBatchEntity,
  VoterTagResponse, VoterTurnoutRecordEntity, VoterVerificationEntity,
  TagVocabularyListResponse, TagVocabularyResponse, TagMergeResult, BulkTagResult,
  VoterLookupResponse,
} from '@/shared/api/types'

/**
 * `voter` endpoints, read from the shipped controllers rather than the plan.
 *
 * Two rules shape this whole module and are enforced here rather than left to
 * each caller:
 *
 * 1. **The list loads unfiltered.** An earlier revision refused to — see backend
 *    prompt 23 §1 for why that was dropped and what stayed behind (the scope
 *    predicate, the PII-free row and the bulk-read audit, none of which needed a
 *    filter to work). `hasFilter()` survives only because the segment builder and
 *    the export dialog still want to know whether anything is narrowing.
 * 2. **PII is a different endpoint, a different permission and an audit entry.**
 *    `voterPii()` is never called to render a row or open a drawer. It is called
 *    when a human presses a button, and the backend writes one audit row per call.
 */

/** The params `VoterController.list` binds. `q` arrives with backend prompt 23. */
export interface VoterFilters {
  q?: string
  unitId?: string
  /**
   * One or many. Repeated in the query string (`?stationId=a&stationId=b`), which is
   * what a Spring `List<UUID>` binds — a campaign comparing three stations should not
   * have to run three searches. Prompt 32 makes the server bind the list; against an
   * older backend the first value wins, which is the previous behaviour.
   */
  stationId?: string | string[]
  wardId?: string
  segmentCode?: string
  supportBand?: string
  registrationStatus?: string
  verificationStatus?: string
  tag?: string
  householdId?: string
  assignedMobilizerId?: string
  updatedSince?: string
  riskBand?: string
  hasOpenIntel?: boolean
}

const FILTER_KEYS: (keyof VoterFilters)[] = [
  'q', 'unitId', 'stationId', 'wardId', 'segmentCode', 'supportBand', 'registrationStatus',
  'verificationStatus', 'tag', 'householdId', 'assignedMobilizerId', 'updatedSince',
  'riskBand', 'hasOpenIntel',
]

/**
 * True when anything is narrowing the list. No longer gates the request — it
 * labels the summary cards ("In this view" vs "In your scope") and tells the
 * empty state whether to say "no matches" or "nothing here yet".
 *
 * `hasOpenIntel: false` is a real narrowing filter — "voters with nothing on
 * file" is a legitimate question — so it counts, which is why this cannot be a
 * plain truthiness check.
 */
export function hasFilter(f: VoterFilters): boolean {
  return FILTER_KEYS.some((k) => f[k] !== undefined && f[k] !== '' && f[k] !== null)
}

export const listVoters = (f: VoterFilters, cursor?: string, size = 50) =>
  api.get<CursorPage<VoterResponse>>(
    `/voters${qs({ ...(f as Record<string, QueryValue>), cursor, size })}`)

export const voterDetail = (id: string) =>
  api.get<VoterDetailResponse>(`/voters/${id}`)

/** `voter:read-pii`. Audited server-side, once per call. Never call on render. */
export const voterPii = (id: string) =>
  api.get<VoterPiiResponse>(`/voters/${id}/pii`)

export const voterDossier = (id: string) =>
  api.get<VoterDossierResponse>(`/voters/${id}/dossier`)

export const searchVoters = (q: string) =>
  api.get<VoterResponse[]>(`/voters/search${qs({ q })}`)

export const voterStatistics = (f: VoterFilters = {}) =>
  api.get<VoterStatisticsResponse>(`/voters/statistics${qs(f as Record<string, QueryValue>)}`)

/** `voter:create`. `pollingStationId` and `consentMethodCode` are both required. */
export const createVoter = (body: unknown) =>
  api.post<VoterDetailResponse>('/voters', body)

export const updateVoter = (id: string, body: unknown, version: number) =>
  api.patch<VoterDetailResponse>(`/voters/${id}`, body, {
    headers: { 'If-Match': String(version) },
  })

// ── engagement ──────────────────────────────────────────────────────────────

export const voterContacts = (id: string) =>
  api.get<VoterContactEntity[]>(`/voters/${id}/contacts`)

export const recordContact = (id: string, body: unknown) =>
  api.post<VoterContactEntity>(`/voters/${id}/contacts`, body)

export const voterConsents = (id: string) =>
  api.get<VoterConsentEntity[]>(`/voters/${id}/consents`)

/** Withdrawal is a POST with `granted: false` — the table is append-only. */
export const recordConsent = (id: string, body: unknown) =>
  api.post<VoterConsentEntity>(`/voters/${id}/consents`, body)

export const setSupport = (id: string, body: unknown) =>
  api.put<VoterDetailResponse>(`/voters/${id}/support`, body)

export const setResidence = (id: string, body: unknown) =>
  api.put<VoterDetailResponse>(`/voters/${id}/residence`, body)

export const setSuppression = (id: string, body: unknown) =>
  api.put<VoterDetailResponse>(`/voters/${id}/suppression`, body)

export const setMobilizer = (id: string, body: unknown) =>
  api.put<VoterDetailResponse>(`/voters/${id}/mobilizer`, body)

export const voterVerifications = (id: string) =>
  api.get<VoterVerificationEntity[]>(`/voters/${id}/verifications`)

export const addVerification = (id: string, body: unknown) =>
  api.post<VoterVerificationEntity>(`/voters/${id}/verifications`, body)

export const voterTurnout = (id: string) =>
  api.get<VoterTurnoutRecordEntity[]>(`/voters/${id}/turnout`)

// ── tags (prompt 40) ────────────────────────────────────────────────────────
//
// Two authorities, on purpose. The three per-voter calls are `voter:update` —
// a mobilizer editing one person's record already holds it. Everything that
// acts on MANY voters at once is `voter:tag`, which the catalogue marks
// dangerous, because bulk-tagging is how a targeting list gets built and a
// merge rewrites thousands of rows.

export const voterTags = (id: string) =>
  api.get<VoterTagResponse[]>(`/voters/${id}/tags`)

/**
 * `voter:update`. The server canonicalises — "Boda Boda" is stored `boda_boda` —
 * and registers the tag in the vocabulary if it is new. The UI shows the
 * canonical form BEFORE the call, because a silent transformation is the whole
 * reason the presets were broken.
 */
export const addTag = (id: string, tag: string) =>
  api.post<VoterTagResponse>(`/voters/${id}/tags`, { tag })

export const removeTag = (id: string, tag: string) =>
  api.del<void>(`/voters/${id}/tags/${encodeURIComponent(tag)}`)

// ── the vocabulary ──────────────────────────────────────────────────────────

/** Not paginated — the response carries `truncated` and `cap` instead. */
export const listTagVocabulary = (params: Record<string, QueryValue> = {}) =>
  api.get<TagVocabularyListResponse>(`/voter-tags${qs(params)}`)

/** `voter:tag`. Creates a tag BEFORE anybody carries it. 409 if it already exists. */
export const createTag = (body: {
  tag: string; displayName?: string; category?: string; description?: string
}) => api.post<TagVocabularyResponse>('/voter-tags', body)

/**
 * `voter:tag`. Labels only.
 *
 * Sending `tag` is a 400, not a silent no-op — the server binds the field
 * solely to refuse it, because Jackson runs with `FAIL_ON_UNKNOWN_PROPERTIES`
 * off and a dropped field would have returned 200 for a rename that never
 * happened. This client never sends it; renaming is `mergeTag`.
 */
export const updateTag = (tag: string, body: {
  displayName?: string; category?: string; description?: string; active?: boolean
}) => api.patch<TagVocabularyResponse>(`/voter-tags/${encodeURIComponent(tag)}`, body)

/**
 * `voter:tag`. Folds one tag into another, and the only supported rename.
 *
 * It rewrites NO targeting rule. The result names every preset and segment that
 * still points at the old tag, and the UI is required to show them — a merge
 * that silently left a preset pointing at a tag nobody carries is the same
 * class of failure as the casing bug.
 */
export const mergeTag = (tag: string, into: string) =>
  api.post<TagMergeResult>(`/voter-tags/${encodeURIComponent(tag)}/merge`, { into })

// ── bulk ────────────────────────────────────────────────────────────────────
//
// Exactly one of `voterIds` and `segmentId`. Both, or neither, is a 400 naming
// the fields — the server refuses to guess which one the caller meant.

export type BulkTagTarget = { voterIds: string[] } | { segmentId: string }

export const bulkApplyTag = (tag: string, target: BulkTagTarget) =>
  api.post<BulkTagResult>(`/voter-tags/${encodeURIComponent(tag)}/apply`, target)

export const bulkRemoveTag = (tag: string, target: BulkTagTarget) =>
  api.post<BulkTagResult>(`/voter-tags/${encodeURIComponent(tag)}/remove`, target)

/** The server's cap on `voterIds`. Mirrored so the UI can page against it. */
export const BULK_TAG_MAX_IDS = 1000

export const voterPlacementHistory = (id: string) =>
  api.get<VoterPlacementHistoryEntity[]>(`/voters/${id}/placement-history`)

/**
 * Correct where a voter is placed in OUR record.
 *
 * Not a transfer. A transfer changes the IEBC roll and is the governed workflow in
 * `relocation` with consent and review; this fixes a mistake in our copy and is why the
 * reason code is mandatory — a placement change with no reason is indistinguishable from
 * a typo six months later.
 */
export const recordPlacement = (id: string, body: {
  toStationId: string
  reasonCode: string
  transferRequestId?: string | null
  notes?: string | null
}) => api.post<VoterDetailResponse>(`/voters/${id}/placement`, body)

// ── import, export, merge, erasure (prompt 18) ──────────────────────────────

/**
 * The CSV template download. Goes through `downloadFile` rather than `api.get`,
 * because `GET /voters/import/template` returns `text/csv` and the shared client
 * parses every body as JSON.
 */
export const downloadImportTemplate = () =>
  downloadFile('/voters/import/template', 'axis-voter-template.csv')

export const submitImport = (body: {
  fileId: string; dryRun: boolean
  mapping: Record<string, unknown>; defaults?: Record<string, unknown>
  attestationRef?: string
}) => api.post<VoterImportBatchEntity>('/voters/import', body)

export const importBatch = (batchId: string) =>
  api.get<VoterImportBatchEntity>(`/voters/import/${batchId}`)

/** National-only and scope-checked server-side — the Phase 2 T2 lesson, applied. */
export const applyImport = (batchId: string) =>
  api.post<void>(`/voters/import/${batchId}/apply`)

export const cancelImport = (batchId: string) =>
  api.post<void>(`/voters/import/${batchId}/cancel`)

export const submitExport = (filter: Record<string, unknown>, columns: string[]) =>
  api.post<VoterExportJobEntity>('/voters/export', { filter, columns })

export const exportJob = (jobId: string) =>
  api.get<VoterExportJobEntity>(`/voters/export/${jobId}`)

/**
 * Run an export to completion and hand the file over.
 *
 * The job is asynchronous because a national export is not a request-response — so this
 * polls, with a ceiling. A caller that polled forever would hang a browser tab on a job
 * the server had already failed.
 */
export async function runExport(
  filter: Record<string, unknown>, columns: string[],
): Promise<{ rowCount: number | null }> {
  const job = await submitExport(filter, columns)
  for (let i = 0; i < 60; i++) {
    const j = await exportJob(job.id)
    if (j.status === 'COMPLETED') {
      if (!j.resultFileId) throw new Error('The export finished but produced no file.')
      await downloadFile(`/files/${j.resultFileId}/download`, 'voters.csv')
      return { rowCount: j.rowCount ?? null }
    }
    if (j.status === 'FAILED' || j.status === 'EXPIRED') {
      throw new Error(`The export ${j.status.toLowerCase()}.`)
    }
    await new Promise((r) => setTimeout(r, 1000))
  }
  throw new Error('The export is taking longer than a minute — check back on it shortly.')
}

export const listDuplicates = () =>
  api.get<VoterDuplicateCandidateEntity[]>('/voters/duplicates')

/** Without `confirm` this returns a preview and writes nothing. */
export const mergeVoter = (id: string, survivingVoterId: string, confirm: boolean) =>
  api.post<Record<string, unknown>>(`/voters/${id}/merge`, { survivingVoterId, confirm })

/** DPA erasure. Hard delete, cascades to the intel subject. */
export const eraseVoter = (id: string) =>
  api.del<void>(`/voters/${id}`)

export const IMPORT_STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pending', VALIDATING: 'Validating', VALIDATED: 'Validated — review then apply',
  APPLYING: 'Applying', COMPLETED: 'Completed', FAILED: 'Failed', CANCELLED: 'Cancelled',
}

/** Columns an export may request. Kept in step with `ExportRequest.columns`. */
export const EXPORT_COLUMNS = [
  'displayName', 'voterNumber', 'supportLevel', 'supportBandCode', 'confidenceCode',
  'verificationStatusCode', 'registrationStatusCode', 'pollingStationId', 'wardId',
  'doNotContact', 'createdAt',
] as const

// ── households and segments ─────────────────────────────────────────────────

export const listHouseholds = (params: { stationId?: string; cursor?: string } = {}) =>
  api.get<CursorPage<VoterHouseholdEntity>>(`/households${qs(params)}`)

export const createHousehold = (body: {
  pollingStationId: string
  name?: string | null
  addressDescription?: string | null
  dwellingTypeCode?: string | null
  notes?: string | null
}) => api.post<VoterHouseholdEntity>('/households', body)

/**
 * Add, remove or re-role one voter in a household.
 *
 * `remove` is what "leave" is on the wire — the household survives losing its last member,
 * because a door that turned out to be empty is a fact worth keeping until somebody says
 * otherwise.
 */
export const manageHouseholdMember = (
  householdId: string,
  body: { voterId: string; action: 'add' | 'remove' | 'assign'; roleCode?: string | null },
) => api.post<VoterHouseholdEntity>(`/households/${householdId}/members`, body)

/**
 * Everyone at a door, including the people who are not on the voter register.
 *
 * A household is a canvassing unit, and the children, non-citizens and unregistered adults
 * behind the door are part of what a visit will meet. They cannot live on `voter_register`
 * — putting a five-year-old there would corrupt every registered, contactable and support
 * count in the platform, and make them a targetable record — so they live here instead,
 * with a name, a relationship and nothing to contact them with.
 */
export interface HouseholdOccupant {
  id: string
  householdId: string
  /** Null for an occupant who is not on the register. */
  voterId?: string | null
  fullName: string
  roleCode: string
  birthYear?: number | null
  registeredVoter: boolean
  notes?: string | null
}

export const listHouseholdOccupants = (householdId: string) =>
  api.get<HouseholdOccupant[]>(`/households/${householdId}/occupants`)

/** Pass `voterId` for somebody on the register, or `fullName` for somebody who is not. */
export const addHouseholdOccupant = (householdId: string, body: {
  voterId?: string | null
  fullName?: string | null
  roleCode: string
  birthYear?: number | null
  notes?: string | null
}) => api.post<HouseholdOccupant>(`/households/${householdId}/occupants`, body)

export const removeHouseholdOccupant = (householdId: string, occupantId: string) =>
  api.del<void>(`/households/${householdId}/occupants/${occupantId}`)

export const HOUSEHOLD_ROLE_LABEL: Record<string, string> = {
  HEAD: 'Head of household', SPOUSE: 'Spouse', CHILD: 'Child', PARENT: 'Parent',
  RELATIVE: 'Relative', TENANT: 'Tenant', OTHER: 'Other',
}

export const DWELLING_TYPE_LABEL: Record<string, string> = {
  SINGLE_ROOM: 'Single room', APARTMENT: 'Apartment', BUNGALOW: 'Bungalow',
  MAISONETTE: 'Maisonette', SHARED_PLOT: 'Shared plot', INFORMAL: 'Informal settlement',
  OTHER: 'Other',
}

// ── contact channels ────────────────────────────────────────────────────────
//
// A channel is the number or address a contact actually travels down, and it is a
// different thing from consent. Consent says we are permitted to phone somebody; a
// channel is the phone number. A voter with consent and no channel is refused
// NO_ACTIVE_CHANNEL — which was every imported voter until these two calls existed.

export const addVoterChannel = (voterId: string, body: {
  kindCode: string
  value: string
  primary: boolean
  verified: boolean
}) => api.post<VoterPiiResponse['channels'][number]>(`/voters/${voterId}/channels`, body)

export const setVoterChannelStatus = (
  voterId: string, channelId: string,
  body: { statusCode?: string; primary?: boolean },
) => api.patch<VoterPiiResponse['channels'][number]>(
  `/voters/${voterId}/channels/${channelId}`, body)

export const CHANNEL_KIND_LABEL: Record<string, string> = {
  PHONE: 'Phone', ALT_PHONE: 'Alternate phone', WHATSAPP: 'WhatsApp', EMAIL: 'Email',
  USSD_MSISDN: 'USSD number',
}

export const CHANNEL_STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Active', BOUNCED: 'Bounced', INVALID: 'Invalid', WITHDRAWN: 'Withdrawn',
}

export const listSegments = () =>
  api.get<VoterSegmentEntity[]>('/voter-segments')

export const previewSegment = (definition: Record<string, unknown>) =>
  api.post<SegmentPreviewResponse>('/voter-segments/preview', { definition })

export const refreshSegment = (id: string) =>
  api.post<VoterSegmentEntity>(`/voter-segments/${id}/refresh`)

// ── labels ──────────────────────────────────────────────────────────────────

export const SUPPORT_BAND_LABEL: Record<string, string> = {
  OPPOSED: 'Opposed', LEAN_OPPOSED: 'Lean opposed', UNDECIDED: 'Undecided',
  LEAN_SUPPORT: 'Lean support', SUPPORTER: 'Supporter', UNASSESSED: 'Unassessed',
}

export const CONFIDENCE_LABEL: Record<string, string> = {
  UNASSESSED: 'Never assessed',
  INFERRED: 'Inferred — not confirmed by anyone',
  REPORTED: 'Reported by a mobilizer',
  VERIFIED: 'Verified',
}

export const AGE_BAND_LABEL: Record<string, string> = {
  A18_24: '18–24', A25_34: '25–34', A35_44: '35–44',
  A45_54: '45–54', A55_64: '55–64', A65_PLUS: '65+',
}

export const VERIFICATION_LABEL: Record<string, string> = {
  UNVERIFIED: 'Unverified', PHONE_VERIFIED: 'Phone verified',
  FIELD_VERIFIED: 'Field verified', ID_SIGHTED: 'ID sighted',
  IEBC_MATCHED: 'IEBC matched', IPRS_MATCHED: 'eCitizen IPRS matched', FAILED: 'Failed',
}

export const REGISTRATION_LABEL: Record<string, string> = {
  REGISTERED: 'Registered', PENDING: 'Pending', TRANSFERRED_OUT: 'Transferred out',
  DECEASED: 'Deceased', INELIGIBLE: 'Ineligible', SUSPENDED: 'Suspended',
}

export const CONTACT_CHANNEL_LABEL: Record<string, string> = {
  DOOR_TO_DOOR: 'Door to door', PHONE_CALL: 'Phone call', SMS: 'SMS',
  WHATSAPP: 'WhatsApp', EMAIL: 'Email', RALLY: 'Rally', MEETING: 'Meeting',
  USSD: 'USSD', WEB_FORM: 'Web form', REFERRAL: 'Referral', OTHER: 'Other',
}

export const CONTACT_OUTCOME_LABEL: Record<string, string> = {
  REACHED: 'Reached', NO_ANSWER: 'No answer', WRONG_NUMBER: 'Wrong number',
  REFUSED: 'Refused', MOVED: 'Moved', DECEASED: 'Deceased',
  CALLBACK_REQUESTED: 'Callback requested', HOSTILE: 'Hostile',
  UNDELIVERABLE: 'Undeliverable',
}

export const CONSENT_PURPOSE_LABEL: Record<string, string> = {
  REGISTER_HOLDING: 'Holding their record', CONTACT_SMS: 'SMS', CONTACT_CALL: 'Calls',
  CONTACT_WHATSAPP: 'WhatsApp', CONTACT_EMAIL: 'Email',
  PROGRAM_ENROLMENT: 'Programme enrolment', TRANSFER_REQUEST: 'Transfer request',
  SENSITIVE_ATTRIBUTES: 'Sensitive attributes', RESEARCH: 'Research',
}

export const SUPPRESSION_LABEL: Record<string, string> = {
  VOTER_REQUEST: 'At their request', DECEASED: 'Deceased', WRONG_PERSON: 'Wrong person',
  HOSTILE: 'Hostile', REGULATORY: 'Regulatory', BOUNCED_REPEATEDLY: 'Repeated bounces',
}

/** Section titles for the dossier, keyed by the backend's section codes. */
export const SECTION_LABEL: Record<string, string> = {
  IDENTITY: 'Identity', PLACEMENT: 'Placement history', RESIDENCE: 'Residence',
  HOUSEHOLD: 'Household', CONTACTS: 'Contact log', SUPPORT: 'Support history',
  CONSENT: 'Consent log', TURNOUT: 'Turnout', VERIFICATION: 'Verification history',
  TAGS_SEGMENTS: 'Tags & segments', TRANSFERS: 'Transfer requests',
  INTEL_TIMELINE: 'Intelligence timeline', SURVEY_RESPONSES: 'Survey responses',
}

/**
 * Blind-index lookup, extended by prompt 24 §7.8 to consult eCitizen IPRS on a
 * local miss.
 *
 * **POST, not GET, and that is deliberate**: a national ID in a query string
 * ends up in access logs, browser history and referer headers. The endpoint used
 * to 404 on a miss and now always returns 200 with `found: false` — do not
 * branch on the status code.
 */
export const lookupVoter = (body: { nationalId?: string; phone?: string }) =>
  api.post<VoterLookupResponse>('/voters/lookup', body)


// ── vocabularies for the record controls ────────────────────────────────────
//
// Sentence case, not Title Case: these are values in a form, not headings. Where a code
// needs explaining rather than translating, the label explains it — "Inferred — not
// confirmed by anyone" tells a mobilizer what they are asserting.

export const SUPPORT_METHOD_LABEL: Record<string, string> = {
  FIELD_REPORT: 'Field report — a conversation', SURVEY: 'Survey response',
  CONTACT_OUTCOME: 'Derived from a contact', IMPORT: 'Imported', ANALYST: 'Analyst judgement',
  MODEL: 'Model estimate',
}

export const CONSENT_METHOD_LABEL: Record<string, string> = {
  SIGNATURE: 'Signature', OTP: 'One-time code', VERBAL_WITNESSED: 'Verbal, witnessed',
  WEB_FORM: 'Web form', USSD: 'USSD', IMPORT_ATTESTED: 'Attested on import', LETTER: 'Letter',
}

export const DIRECTION_LABEL: Record<string, string> = {
  OUTBOUND: 'We contacted them', INBOUND: 'They contacted us',
}

export const SENTIMENT_LABEL: Record<string, string> = {
  POSITIVE: 'Positive', NEUTRAL: 'Neutral', NEGATIVE: 'Negative', HOSTILE: 'Hostile',
}

export const RESIDENCE_SOURCE_LABEL: Record<string, string> = {
  SELF_DECLARED: 'They told us', FIELD_CAPTURE: 'Captured in the field',
  GPS_CAPTURE: 'GPS at the door', IMPORT: 'Imported', INFERRED: 'Inferred',
}

export const PLACEMENT_REASON_LABEL: Record<string, string> = {
  DATA_CORRECTION: 'Data correction — our record was wrong',
  BOUNDARY_REVIEW: 'Boundary review', STATION_DISSOLVED: 'Station dissolved',
  REGISTRATION: 'Registration', IEBC_TRANSFER: 'IEBC transfer',
  TRANSFER_RECORDED: 'Transfer recorded', MERGE: 'Merge',
}

export const TURNOUT_EVIDENCE_LABEL: Record<string, string> = {
  IEBC_REGISTER: 'IEBC register', AGENT_MARKED: 'Marked by an agent',
  SELF_REPORTED: 'Self-reported', INFERRED: 'Inferred',
}

export const ELECTION_TYPE_LABEL: Record<string, string> = {
  GENERAL: 'General', BY_ELECTION: 'By-election', REFERENDUM: 'Referendum', PRIMARY: 'Primary',
}
